/* eslint-disable react/jsx-filename-extension */
import { onCloseMenu } from '@src/actions/mainmenu';
import {
  CheckCircleIcon,
  ContactsIcon,
  DeviceHubIcon,
  DevicesIcon,
  EmojiObjectsIcon,
  FactCheckIcon,
  ForumIcon,
  GroupIcon,
  PodcastIcon,
  ScatterPlotIcon,
  SellIcon,
  TrendingUpIcon,
  TuneIcon,
} from '@src/components/AdminHome/Common/SideNav/SvgIcons';
import { useSubscriptionsConditions } from '@src/components/AdminHome/Subscription/useSubscriptionConditions';
import BackendProxy from '@src/logic/backendproxy';
import { MessagingProvider } from '@src/model/frontendmodel';
import { fetchNotificationTimeLimit } from '@src/reducers/chats';
import { ActionMode } from '@src/reducers/chats/initialState';
import { useGetAIConversationReportAvailabilityQuery } from '@src/reducers/aiConversationReportApi';
import CallStartIcon from '@src/resources/icons/CallStartIcon';
import QuickreplyOutlinedIcon from '@src/resources/icons/QuickreplyIcon';
import RobotIcon from '@src/resources/icons/RobotIcon';
import { timeLimit } from '@src/selectors/chats';
import { pagesWithoutAccessSelector } from '@src/selectors/rol';
import { selectAuthenticationInfo } from '@src/selectors/user';
import type { RootState } from '@src/store/reduxstore';
import { NotificationStatus } from '@src/types/chats';
import {
  pathAutomaticMessages,
  pathBusinessTime,
  pathChatsBehavior,
  pathGeneralsConfig,
  pathIntegrations,
  pathManageSubscription,
  pathQuickMessages,
  urlAgentConsole,
  urlAnalyticsAgentChats,
  urlAnalyticsAgentNotifications,
  urlAnalyticsAIConversations,
  urlAnalyticsAuditExports,
  urlAnalyticsCallConsumptionReport,
  urlAnalyticsChannelChats,
  urlAnalyticsChatsByDepartment,
  urlAnalyticsChatsGeneral,
  urlAnalyticsChatTags,
  urlAnalyticsChatTimes,
  urlAnalyticsChatTransfers,
  urlAnalyticsContactTags,
  urlAnalyticsCustomerSatisfactionBinary,
  urlAnalyticsCustomerSatisfactionStars,
  urlAnalyticsDailyMessages,
  urlAnalyticsFaqs,
  urlAnalyticsLoginLogout,
  urlAnalyticsServiceMessages,
  urlAnalyticsUniqueContacts,
  urlAnalyticsWAConversations,
  urlAnalyticsWhatsappTemplates,
  urlAnalyticsWhatsappTraffic,
  urlAssistant,
  urlAuditV2,
  urlBatchMessages,
  urlBatchMessagesGeneralReport,
  urlCalls,
  urlChannelFbAccount,
  urlChannelInstagramAccount,
  urlChannelLivechatAccount,
  urlChannels,
  urlChannelTelegramAccount,
  urlChannelWppSetup,
  urlChatsManagement,
  urlContact,
  urlCustomFields,
  urlDepartments,
  urlFlowBuilder,
  urlFaqsQA,
  urlFaqsSetup,
  urlMessagesSetup,
  urlMyAccount,
  urlProfileSettings,
  urlSatisfactionConfiguration,
  urlSecuritySettings,
  urlTagsContactManagement,
  urlTagsConverManagement,
  urlUser,
} from '@src/utils/adminRoutes';
import { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useBlockedRoutesByRol from './useBlockedRoutesByRol';

export type SideNavItemsOption = {
  url: string;
  title?: string;
  icon?: JSX.Element;
  subtitle?: string;
  childrens?: SideNavItemsOptionChild[];
  action?: () => void;
  subPaths?: SideNavItemsOptionWithSubPaths[];
};

export type SideNavItemsOptionChild = {
  url: string;
  title: string;
  subtitle?: string;
  action?: () => void;
};

export type SideNavItemsOptionWithSubPaths = {
  url: string;
  title: string;
};

const useSideNavAdminOptions = (): SideNavItemsOption[] => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { blockedRoutes, mandatoryBlockedRoutes } = useBlockedRoutesByRol();

  const [showWppAcquisition, setShowWppAcquisition] = useState(false);

  const user = useSelector(selectAuthenticationInfo);
  const pagesWithoutAccessUser = useSelector(pagesWithoutAccessSelector);
  const aiReport = useGetAIConversationReportAvailabilityQuery().data;

  const isNotificationStatusActive =
    useSelector(timeLimit).status === NotificationStatus.ACTIVE;
  const menu: { close?: boolean } = useSelector(
    ({ adminHome }: RootState) => adminHome.menu
  );
  const { deptsEarlyAccess } = useSelector(
    ({ generalProperties }: RootState) => generalProperties
  );

  const handleCloseMenuFromItems = () => {
    if (menu.close) dispatch(onCloseMenu());
  };

  const buildDemoQuery = () =>
    user?.employer?.name === 'Instagram Demo' ? '?demomode=instagram' : '';

  const redirectTo = (url: string) => {
    handleCloseMenuFromItems();
    history.push(url + buildDemoQuery());
  };

  const subscribeConditions = useSubscriptionsConditions();

  useEffect(() => {
    Promise.all([
      BackendProxy.getBotAccountsByProvider(MessagingProvider.WHATSAPPOFF),
      BackendProxy.getBotAccountsByProvider(MessagingProvider.WHATSAPPBM),
      BackendProxy.getBotAccountsByProvider(MessagingProvider.WHATSAPP360),
      BackendProxy.getBotAccountsByProvider(MessagingProvider.WHATSAPPB2CHAT),
      dispatch(fetchNotificationTimeLimit({ mode: ActionMode.GET })),
    ]).then(responses => {
      // only for WHATSAPPB2CHAT
      setShowWppAcquisition(responses[3].length > 0);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAdminRole = user?.admin;
  const urlFaqsByDefault = isAdminRole ? urlFaqsSetup : urlAnalyticsFaqs;
  const urlSatisfactionByDefault = isAdminRole
    ? urlSatisfactionConfiguration
    : urlAnalyticsCustomerSatisfactionBinary;

  type SideNavItem = {
    url: string;
    title: string;
    subtitle?: string;
    action?: () => void;
    icon: ReactElement;
    childrens?: Omit<SideNavItem, 'icon'>[];
    subPaths?: SideNavItemsOptionWithSubPaths[];
  };

  const sideNavItems: SideNavItem[] = [
    // 0 Audit
    {
      url: urlAuditV2,
      title: 'MainMenu.auditV2Option',
      subtitle: 'Audit.headSubtitle',
      icon: CheckCircleIcon,
      action: () => redirectTo(urlAuditV2),
    },
    // 1 Contacts
    {
      url: urlContact,
      title: 'MainMenu.contactOption',
      subtitle: 'Contact.headSubtitle',
      icon: ContactsIcon,
      action: () => redirectTo(urlContact),
    },
    // 2 Reports
    {
      url: urlAnalyticsChatsGeneral,
      title: 'MainMenu.analyticsOption',
      icon: TrendingUpIcon,
      action: () => redirectTo(urlAnalyticsChatsGeneral),
      childrens: [
        // 4-0 AnalyticsChatsGeneral
        {
          url: urlAnalyticsChatsGeneral,
          title: 'MainMenu.AnalyticsChatsGeneral',
          subtitle: 'MainMenu.AnalyticsChatsGeneral.Subtitle',
          action: () => redirectTo(urlAnalyticsChatsGeneral),
        },

        // 4-1 AnalyticsChatTimes
        {
          url: urlAnalyticsChatTimes,
          title: 'MainMenu.AnalyticsChatTimes',
          subtitle: 'MainMenu.AnalyticsChatTimes.Subtitle',
          action: () => redirectTo(urlAnalyticsChatTimes),
        },
        // 4-2 AnalyticsAgentChats
        {
          url: urlAnalyticsAgentChats,
          title: 'MainMenu.AnalyticsChatAgents',
          subtitle: 'MainMenu.AnalyticsChatAgents.Subtitle',
          action: () => redirectTo(urlAnalyticsAgentChats),
        },
        // 4-3 AnalyticsChannelChats
        {
          url: urlAnalyticsChannelChats,
          title: 'MainMenu.AnalyticsChatChannels',
          subtitle: 'MainMenu.AnalyticsChatChannels.Subtitle',
          action: () => redirectTo(urlAnalyticsChannelChats),
        },
        // TODO: disable for now
        // 4-3.2 AnalyticsCallConsumptionReport
        // eslint-disable-next-line no-constant-condition
        ...(false
          ? [
              {
                url: urlAnalyticsCallConsumptionReport,
                title: 'MainMenu.AnalyticsCallConsumptionReport',
                subtitle: '',
                action: () => redirectTo(urlAnalyticsCallConsumptionReport),
              },
            ]
          : []),
        // 4-4 AnalyticsDailyMessages
        {
          url: urlAnalyticsDailyMessages,
          title: 'MainMenu.AnalyticsDailyMessages',
          subtitle: 'MainMenu.AnalyticsDailyMessages.Subtitle',
          action: () => redirectTo(urlAnalyticsDailyMessages),
        },
        // 4-5 AnalyticsChatTransfers
        {
          url: urlAnalyticsChatTransfers,
          title: 'MainMenu.AnalyticsChatTransfers',
          subtitle: 'MainMenu.AnalyticsChatTransfers.Subtitle',
          action: () => redirectTo(urlAnalyticsChatTransfers),
        },
        // 4-6 AnalyticsLoginLogout
        {
          url: urlAnalyticsLoginLogout,
          title: 'MainMenu.AnalyticsLoginLogout',
          subtitle: 'MainMenu.AnalyticsLoginLogout.Subtitle',
          action: () => redirectTo(urlAnalyticsLoginLogout),
        },
        // 4-7 AnalyticsAuditExports
        {
          url: urlAnalyticsAuditExports,
          title: 'MainMenu.AnalyticsAuditExports',
          subtitle: 'MainMenu.AnalyticsAuditExports.Subtitle',
          action: () => redirectTo(urlAnalyticsAuditExports),
        },
        // 4-8 AnalyticsChatTags
        {
          url: urlAnalyticsChatTags,
          title: 'MainMenu.AnalyticsChatTags',
          subtitle: 'MainMenu.AnalyticsChatTags.Subtitle',
          action: () => redirectTo(urlAnalyticsChatTags),
        },
        // 4-9 AnalyticsChatsByDepartment
        {
          url: urlAnalyticsChatsByDepartment,
          title: 'MainMenu.AnalyticsDepartments',
          subtitle: 'MainMenu.AnalyticsChatsByDepartment.Subtitle',
          action: () => redirectTo(urlAnalyticsChatsByDepartment),
        },

        // 4-10 AnalyticsWAConversations
        {
          url: urlAnalyticsWAConversations,
          title: 'MainMenu.WhatsappConversations',
          subtitle: '',
          action: () => redirectTo(urlAnalyticsWAConversations),
        },

        // 4-10-1 AnalyticsServiceMessages
        {
          url: urlAnalyticsServiceMessages,
          title: 'MainMenu.ServiceMessages',
          subtitle: '',
          action: () => redirectTo(urlAnalyticsServiceMessages),
        },

        // 4-10-2 AnalyticsWAConversations
        {
          url: urlAnalyticsUniqueContacts,
          title: 'MainMenu.UniqueContacts',
          subtitle: '',
          action: () => redirectTo(urlAnalyticsUniqueContacts),
        },

        // 4-10-3 AnalyticsAIConversations (F003 RN07: AI billing active only)
        ...(aiReport?.available
          ? [
              {
                url: urlAnalyticsAIConversations,
                title: 'MainMenu.AIConversations',
                subtitle: '',
                action: () => redirectTo(urlAnalyticsAIConversations),
              },
            ]
          : []),

        // 4-13 AnalyticsWAAcquisition
        ...(showWppAcquisition
          ? [
              {
                url: urlAnalyticsWhatsappTraffic,
                title: 'MainMenu.AnalyticsWppAcquisition',
                subtitle: 'MainMenu.AnalyticsWppAcquisition.Subtitle',
                action: () => redirectTo(urlAnalyticsWhatsappTraffic),
              },
            ]
          : []),
        // 4-14 AnalyticsWppTemplates
        {
          url: urlAnalyticsWhatsappTemplates,
          title: 'MainMenu.AnalyticsWppTemplates',
          action: () => redirectTo(urlAnalyticsWhatsappTemplates),
        },
        // 4-15
        {
          url: urlAnalyticsContactTags,
          title: 'ContactsByTags',
          subtitle: 'MainMenu.AnalyticsContactTags.Subtitle',
          action: () => redirectTo(urlAnalyticsContactTags),
        },
        // 4-16 Analytics Agent notifications
        ...(isNotificationStatusActive
          ? [
              {
                url: urlAnalyticsAgentNotifications,
                title: 'MainMenu.AnalyticsAgentNotifications',
                subtitle: '',
                action: () => redirectTo(urlAnalyticsAgentNotifications),
              },
            ]
          : []),
      ],
    },
    // 3 Massive Messages
    {
      url: urlBatchMessages,
      title: 'MainMenu.BatchMessages',
      subtitle: 'DeliveriesMassive.PageSubtitle',
      icon: ScatterPlotIcon,
      action: () => redirectTo(urlBatchMessages),
      childrens: [
        {
          url: urlBatchMessages,
          title: 'DeliveriesMassive.Tab.Management',
          action: () => redirectTo(urlBatchMessages),
        },
        {
          url: urlBatchMessagesGeneralReport,
          title: 'DeliveriesMassive.Tab.ApiReport',
          action: () => redirectTo(urlBatchMessagesGeneralReport),
        },
      ],
    },
    // 4 Chats management
    {
      url: urlChatsManagement,
      title: 'MainMenu.ChatsManagement',
      icon: ForumIcon,
      action: () => redirectTo(urlChatsManagement),
    },
    // 5 Departments
    ...(deptsEarlyAccess
      ? [
          {
            title: 'MainMenu.departments',
            icon: DeviceHubIcon,
            url: urlDepartments,
            action: () => redirectTo(urlDepartments),
            subPaths: [
              {
                url: urlFlowBuilder,
                title: 'MainMenu.flowBuilder',
              },
            ],
          },
        ]
      : []),
    // 6 Assistant
    {
      url: urlAssistant,
      title: 'MainMenu.assistant',
      icon: <RobotIcon />,
      action: () => redirectTo(urlAssistant),
    },
    // 7 Quick Messages
    {
      url: pathQuickMessages,
      title: 'MainMenu.quickMessages',
      icon: <QuickreplyOutlinedIcon />,
      action: () => redirectTo(pathQuickMessages),
    },
    // 8 Channels
    {
      url: urlChannels,
      title: 'MainMenu.channels',
      icon: PodcastIcon,
      action: () => redirectTo(urlChannels),
      subPaths: [
        {
          url: urlChannelWppSetup,
          title: 'Channel.WhatsAppSetup.Subpath.title',
        },
        {
          url: urlChannelTelegramAccount,
          title: 'Channel.TelegramSetup.Subpath.title',
        },
        {
          url: urlChannelFbAccount,
          title: 'Channel.FBAccountSetup.Subpath.title',
        },
        {
          url: urlChannelLivechatAccount,
          title: 'Channel.LivechatSetup.Subpath.title',
        },
        {
          url: urlChannelInstagramAccount,
          title: 'Channel.InstagramSetup.Subpath.title',
        },
      ],
    },
    // Calls
    {
      url: urlCalls,
      title: 'MainMenu.Calls',
      icon: <CallStartIcon />,
      action: () => redirectTo(urlCalls),
    },
    // 9 FAQSBot
    {
      url: urlFaqsSetup,
      title: 'MainMenu.solutionOption',
      icon: FactCheckIcon,
      action: () => redirectTo(urlFaqsByDefault),
      childrens: [
        // 5-0 FaqsSetup
        {
          url: urlFaqsSetup,
          title: 'MainMenu.solutionConfigurationOption',
          action: () => redirectTo(urlFaqsSetup),
        },
        // 5-1 FaqsQA
        {
          url: urlFaqsQA,
          title: 'MainMenu.solutionFAQOption',
          subtitle: 'MainMenu.solutionFAQSubtitle',
          action: () => redirectTo(urlFaqsQA),
        },
        // 5-2 AnalyticsFaqs
        {
          url: urlAnalyticsFaqs,
          title: 'MainMenu.AnalyticsFaqs',
          subtitle: 'MainMenu.AnalyticsFaqs.Subtitle',
          action: () => redirectTo(urlAnalyticsFaqs),
        },
      ],
    },
    // 10 Satisfaction Bot
    {
      url: urlSatisfactionConfiguration,
      title: 'MainMenu.satisfactionOption',
      icon: EmojiObjectsIcon,
      action: () => redirectTo(urlSatisfactionByDefault),
      childrens: [
        // 6-0 SatisfactionConfiguration
        {
          url: urlSatisfactionConfiguration,
          title: 'MainMenu.botConfiguration',
          action: () => redirectTo(urlSatisfactionConfiguration),
        },
        // 6-1 AnalyticsCustomerSatisfactionBinary
        {
          url: urlAnalyticsCustomerSatisfactionBinary,
          title: 'MainMenu.AnalyticsCustomerSatisfactionBinary',
          subtitle: 'MainMenu.AnalyticsCustomerSatisfactionBinary.Subtitle',
          action: () => redirectTo(urlAnalyticsCustomerSatisfactionBinary),
        },
        // 6-2 AnalyticsCustomerSatisfactionStars
        {
          url: urlAnalyticsCustomerSatisfactionStars,
          title: 'MainMenu.AnalyticsCustomerSatisfactionStars',
          subtitle: 'MainMenu.AnalyticsCustomerSatisfactionStars.Subtitle',
          action: () => redirectTo(urlAnalyticsCustomerSatisfactionStars),
        },
      ],
    },
    // 11 Tags
    {
      url: urlTagsConverManagement,
      title: 'MainMenu.TagsManagement',
      icon: SellIcon,
      action: () => redirectTo(urlTagsConverManagement),
      childrens: [
        {
          url: urlTagsConverManagement,
          title: 'MainMenu.TagsManagement.conversation.title',
          action: () => redirectTo(urlTagsConverManagement),
        },
        {
          url: urlTagsContactManagement,
          title: 'MainMenu.TagsManagement.contacts.title',
          action: () => redirectTo(urlTagsContactManagement),
        },
      ],
    },
    // 12 Users
    {
      url: urlUser,
      title: 'MainMenu.userOption',
      icon: GroupIcon,
      action: () => redirectTo(urlUser),
    },
    // 13 General Settings
    {
      title: 'MainMenu.configurations',
      icon: TuneIcon,
      url: '',
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      action: () => {},
      childrens: [
        // 7-0 Automatic messages
        {
          url: urlMessagesSetup,
          title: 'MainMenu.automaticMessages',
          action: () => redirectTo(urlMessagesSetup),
        },
        // 7-1 Custom fields
        {
          url: urlCustomFields,
          title: 'MainMenu.customFieldsOption',
          action: () => redirectTo(urlCustomFields),
        },
        // 7-2 Business time
        {
          url: pathBusinessTime,
          title: 'MainMenu.businessTimeOption',
          action: () => redirectTo(pathBusinessTime),
        },
        // 7-3 Followup Messages
        {
          url: pathAutomaticMessages,
          title: 'MainMenu.FollowupMessages',
          subtitle: 'AutomaticMessages.PageSubtitle',
          action: () => redirectTo(pathAutomaticMessages),
        },
        // 7-4 Chats behavior
        {
          url: pathChatsBehavior,
          title: 'MainMenu.ChatsBehavior',
          subtitle: '',
          action: () => redirectTo(pathChatsBehavior),
        },
        // 7-5 Integrations
        {
          url: pathIntegrations,
          title: 'MainMenu.Integrations',
          action: () => redirectTo(pathIntegrations),
        },
        // 7-6 Security settings
        {
          url: urlSecuritySettings,
          title: 'MainMenu.securitySettingsOption',
          action: () => redirectTo(urlSecuritySettings),
        },
        // 7-7 Generals
        {
          url: pathGeneralsConfig,
          title: 'MainMenu.Generals',
          subtitle: '',
          action: () => redirectTo(pathGeneralsConfig),
        },
        // 7-8 Admin Subscription
        ...(subscribeConditions.showCancelSection ||
        subscribeConditions.showPaymentSection ||
        subscribeConditions.showPlanSection ||
        subscribeConditions.showTCSection
          ? [
              {
                url: pathManageSubscription,
                title: 'MainMenu.ManageSubscription',
                action: () => redirectTo(pathManageSubscription),
              },
            ]
          : []),
        // 7-9 My account
        {
          url: urlMyAccount,
          title: 'MainMenu.myAccountOption',
          action: () => redirectTo(urlMyAccount),
        },
        // 7-2 My profile
        {
          url: urlProfileSettings,
          title: 'MainMenu.myProfileSettingsOption',
          action: () => redirectTo(urlProfileSettings),
        },
      ],
    },
    // 14 Agent Console
    {
      url: urlAgentConsole,
      title: 'MainMenu.consoleOption',
      icon: DevicesIcon,
      action: () => redirectTo(urlAgentConsole),
    },
  ];

  return sideNavItems.reduce((acc: SideNavItem[], route: SideNavItem) => {
    const blocked = pagesWithoutAccessUser?.length
      ? [...pagesWithoutAccessUser, ...mandatoryBlockedRoutes]
      : [...blockedRoutes, ...mandatoryBlockedRoutes];

    const isBlockedRoute = blocked.includes(route.url);
    const unlockedRouteChildrens =
      route?.childrens?.filter(subroute => !blocked.includes(subroute.url)) ||
      [];

    if (unlockedRouteChildrens?.length > 0) {
      route.childrens = unlockedRouteChildrens;
      route.url = unlockedRouteChildrens[0]?.url;
      route.action = unlockedRouteChildrens[0]?.action;
    }

    if (unlockedRouteChildrens?.length > 0 || !isBlockedRoute) acc.push(route);

    return acc;
  }, []);
};

export default useSideNavAdminOptions;
